// Slim vs full submission predicates — the single source of truth for the
// clinical-history lazy-loading model (SHER-483/492/495/590).
//
// A "slim" submission is a metadata-only placeholder: the list load
// (`fetchSlimSubmissionsPatientAction`) groups flat rows into the store's
// `surveys[]` shape with `surveyRecords: []` and a `__slim` flag, so opening a
// patient no longer assembles the whole history. Full submissions (the on-demand
// / bulk loads) carry real `surveyRecords` and never set `__slim`.
//
// Every read site used to re-derive "is this slim?" inline in a slightly
// different shape; these predicates keep that judgement in one place. See
// docs/features/medical-record-requests/clinical-history-loading.md.

type SubmissionLike = { __slim?: boolean } | null | undefined;
type SurveyLike = { submissions?: SubmissionLike[] } | null | undefined;

/** A metadata-only placeholder submission (no `surveyRecords`). */
export const isSlimSubmission = (submission: SubmissionLike): boolean => !!submission?.__slim;

/** A survey whose submissions include at least one slim (placeholder) row. */
export const isSlimSurvey = (survey: SurveyLike): boolean =>
    (survey?.submissions ?? []).some(isSlimSubmission);

/** A submission that carries its real `surveyRecords`. */
export const isFullSubmission = (submission: SubmissionLike): boolean =>
    !!submission && !isSlimSubmission(submission);

/** A survey that already holds at least one full (non-slim) submission. */
export const isFullSurvey = (survey: SurveyLike): boolean =>
    (survey?.submissions ?? []).some(isFullSubmission);
